import { ReactComponent as OutlineLink } from "assets/svg/outline-link.svg";
import { ReactComponent as OutlineDownload16 } from "assets/svg/outline-download-16.svg";
import { useState } from "react";
import { Button } from "react-bootstrap";

import { useStripeModal } from "components/Dashboard/DoctorDashboard/contexts/stripe-modal-context";

import ICalCalendar from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/icalendar";
import { useSubscription } from "contexts/subscription-context";

import { AmplitudePageName, track } from "utils/amplitude";
import styles from "./Integration.module.scss";
import IconButton from "components/icon-button";
import loginToStripe from "services/loginToStripe";
import { useUser } from "contexts/UserContext";
const AMPLITUDE_PAGE_NAME: AmplitudePageName = "pro-settings-integration";

const StripeButton = () => {
  const { user } = useUser();
  const { openModal } = useStripeModal();

  const goToStripe = async () => {
    try {
      const { url } = await loginToStripe();
      window.open(url, "_blank");
    } catch (error) {
      console.error(error);
    }
  };

  return !user?.has_stripe_account_connected ? (
    <Button
      className={styles["integration__stripe-connect"]}
      onClick={() => {
        track(AMPLITUDE_PAGE_NAME, "learn-more-button-stripe", "click");
        openModal({ amplitudePageName: AMPLITUDE_PAGE_NAME });
      }}
    >
      <OutlineLink />
      Connect
    </Button>
  ) : (
    <Button
      variant={"outline-primary"}
      className={styles["integration__stripe-connect"]}
      onClick={() => {
        track(AMPLITUDE_PAGE_NAME, "learn-more-button-stripe", "click");
        goToStripe();
      }}
    >
      Go to Stripe
    </Button>
  );
};

const OutlookButton = () => {
  const { user } = useUser();
  const { canAccessFeature } = useSubscription();
  const [showConnectICalendar, setShowConnectICalendar] = useState(false);

  const onClickConnect = () => {
    if (canAccessFeature("calendar-sync")) {
      setShowConnectICalendar(true);
    }
  };

  if (!user?.has_outlook_connected) {
    return (
      <>
        <Button
          className={styles["integration__outlook-connect"]}
          onClick={() => {
            track(AMPLITUDE_PAGE_NAME, "connect-button-outlook", "click");
            onClickConnect();
          }}
        >
          <OutlineLink />
          Connect
        </Button>
        <ICalCalendar
          showConnectICalendar={showConnectICalendar}
          setShowConnectICalendar={setShowConnectICalendar}
          analyticsPageName={AMPLITUDE_PAGE_NAME}
        />
      </>
    );
  }

  return (
    <>
      <Button
        className={styles["integration__outlook-connect"]}
        onClick={() => {
          track(AMPLITUDE_PAGE_NAME, "connect-button-outlook", "click");
          onClickConnect();
        }}
      >
        <OutlineLink />
        Connect
      </Button>
      <ICalCalendar
        showConnectICalendar={showConnectICalendar}
        setShowConnectICalendar={setShowConnectICalendar}
        analyticsPageName={AMPLITUDE_PAGE_NAME}
      />
    </>
  );
};

const ComingSoonConnectButton = () => {
  return (
    <IconButton
      onClick={() =>
        track(AMPLITUDE_PAGE_NAME, "coming-soon-connect-button", "click")
      }
      className={styles["integration__gray-connect-text"]}
    >
      Coming soon
    </IconButton>
  );
};

const DownloadChromeExtensionButton = () => {
  const downloadLink =
    "https://chromewebstore.google.com/detail/orchid-ai-clinical-notes/llecjojlphipbakjmafcpapcccilelkj";

  return (
    <Button
      as={"a"}
      href={downloadLink}
      target={"_blank"}
      rel={"noopener noreferrer"}
      className={styles["integration__blue-connect-text"]}
      onClick={() =>
        track(AMPLITUDE_PAGE_NAME, "download-chrome-extension-button", "click")
      }
    >
      <OutlineDownload16 />
      Install
    </Button>
  );
};

type Props = {
  appLabel: string;
};

const IntegrationConnectButton = ({ appLabel }: Props) => {
  switch (appLabel) {
    case "Chrome Browser Extension":
      return <DownloadChromeExtensionButton />;
    case "Stripe":
      return <StripeButton />;
    case "Google Meet":
      return <ComingSoonConnectButton />;
    case "Outlook":
      return <OutlookButton />;
    case "Zoom":
      return <ComingSoonConnectButton />;
    case "Gusto":
      return <ComingSoonConnectButton />;
    case "Quickbooks Online":
      return <ComingSoonConnectButton />;
    case "Xero":
      return <ComingSoonConnectButton />;
    default:
      return null;
  }
};

export { IntegrationConnectButton };
